import { getAttendanceDaySummary } from '@/features/kitchen/storage/attendanceStorage'
import { getDispatchSummary } from '@/features/kitchen/storage/dispatchStorage'
import { getProductionSummary } from '@/features/kitchen/storage/productionStorage'
import { getWasteSummary } from '@/features/kitchen/storage/wasteStorage'
import { kitchenTerms } from '@/features/kitchen/kitchenTerms'

export type HomeActionPriority = 'high' | 'medium' | 'low'

export type HomeActionItem = {
  id: string
  priority: HomeActionPriority
  module: 'attendance' | 'production' | 'dispatch' | 'waste'
  title: string
  detail: string
  to: string
}

export type KitchenHomeSummary = {
  actions: HomeActionItem[]
  attendance: ReturnType<typeof getAttendanceDaySummary>
  production: ReturnType<typeof getProductionSummary>
  dispatch: ReturnType<typeof getDispatchSummary>
  waste: ReturnType<typeof getWasteSummary>
  generatedAt: string
}

const TEAM_SHIFT_START_HOUR = 6
const TEAM_SHIFT_START_MINUTE = 0

function isPastTeamShiftStart(now: Date): boolean {
  const start = new Date(now)
  start.setHours(TEAM_SHIFT_START_HOUR, TEAM_SHIFT_START_MINUTE, 0, 0)
  return now.getTime() > start.getTime()
}

function firstNames(names: string[], limit = 3): string {
  return names
    .slice(0, limit)
    .map((name) => name.split(' ')[0])
    .join(', ')
}

export function getKitchenHomeSummary(
  now = new Date(),
  overrides?: {
    production?: ReturnType<typeof getProductionSummary>
    dispatch?: ReturnType<typeof getDispatchSummary>
    waste?: ReturnType<typeof getWasteSummary>
    attendance?: ReturnType<typeof getAttendanceDaySummary>
  },
): KitchenHomeSummary {
  const attendance = overrides?.attendance ?? getAttendanceDaySummary()
  const production = overrides?.production ?? getProductionSummary()
  const dispatch = overrides?.dispatch ?? getDispatchSummary()
  const waste = overrides?.waste ?? getWasteSummary()

  const actions: HomeActionItem[] = []

  if (attendance.notCheckedInCount > 0) {
    actions.push({
      id: 'attendance-missing',
      priority: isPastTeamShiftStart(now) ? 'high' : 'medium',
      module: 'attendance',
      title: `${attendance.notCheckedInCount} orang belum ${kitchenTerms.attendance.checkInVerb}`,
      detail: firstNames(attendance.notCheckedIn) || 'Tim dapur',
      to: '/kitchen/attendance',
    })
  }

  if (attendance.onShift.length > 0) {
    actions.push({
      id: 'attendance-on-shift',
      priority: 'low',
      module: 'attendance',
      title: `${attendance.onShift.length} orang ${kitchenTerms.attendance.onShift.toLowerCase()}`,
      detail: `Siap ${kitchenTerms.attendance.checkOutVerb} saat pulang`,
      to: '/kitchen/attendance',
    })
  }

  if (production.openSessionCount > 0) {
    actions.push({
      id: 'production-open',
      priority: production.inProgress > 0 ? 'medium' : 'high',
      module: 'production',
      title:
        production.inProgress > 0
          ? `${production.inProgress} input produksi belum masuk stok`
          : `${production.drafts} draft produksi perlu dilanjutkan`,
      detail: `Plan HO ${production.planPct}% tercapai (${production.actualTotal}/${production.planTotal} pcs)`,
      to: '/kitchen/production',
    })
  } else if (production.planPct < 100) {
    actions.push({
      id: 'production-plan',
      priority: 'medium',
      module: 'production',
      title: `Plan produksi ${production.planPct}%`,
      detail: 'Belum ada sesi aktif hari ini',
      to: '/kitchen/production',
    })
  }

  if (dispatch.preparedCount > 0) {
    actions.push({
      id: 'dispatch-prepared',
      priority: 'high',
      module: 'dispatch',
      title: `${dispatch.preparedCount} pengiriman siap diserahkan`,
      detail: firstNames(dispatch.pendingStores) || 'Ke delman',
      to: '/kitchen/dispatch',
    })
  }

  if (dispatch.pendingCount > dispatch.preparedCount) {
    actions.push({
      id: 'dispatch-draft',
      priority: 'medium',
      module: 'dispatch',
      title: `${dispatch.pendingCount - dispatch.preparedCount} pengiriman perlu dilengkapi`,
      detail: kitchenTerms.dispatch.completeAndHandover,
      to: '/kitchen/dispatch',
    })
  }

  if (waste.readyExecuteCount > 0) {
    actions.push({
      id: 'waste-execute',
      priority: 'high',
      module: 'waste',
      title: `${waste.readyExecuteCount} batch siap dieksekusi`,
      detail: 'Sudah disetujui HO — butuh selfie & PIN',
      to: '/kitchen/waste',
    })
  }

  if (waste.draftCount > 0) {
    actions.push({
      id: 'waste-draft',
      priority: 'low',
      module: 'waste',
      title: `${waste.draftCount} proposal ${kitchenTerms.modules.waste.toLowerCase()} draft`,
      detail: 'Lengkapi lalu kirim ke HO',
      to: '/kitchen/waste',
    })
  }

  if (waste.rejectedCount > 0) {
    actions.push({
      id: 'waste-rejected',
      priority: 'medium',
      module: 'waste',
      title: `${waste.rejectedCount} proposal ditolak HO`,
      detail: 'Perbaiki dan buat ulang proposal',
      to: '/kitchen/waste',
    })
  }

  const priorityOrder: Record<HomeActionPriority, number> = { high: 0, medium: 1, low: 2 }
  actions.sort((a, b) => priorityOrder[a.priority] - priorityOrder[b.priority])

  const hasUrgent = actions.some((action) => action.priority === 'high' || action.priority === 'medium')
  const visibleActions = hasUrgent ? actions.filter((action) => action.priority !== 'low') : actions

  return {
    actions: visibleActions,
    attendance,
    production,
    dispatch,
    waste,
    generatedAt: now.toISOString(),
  }
}
